import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { interval, Subject, Subscription } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { AppState } from '../../../state/app.state';
import * as IcpSelectors from '../../../state/icp/icp.selectors';
import * as OnboardingSelectors from '../../../state/onboarding/onboarding.selectors';
import { selectDashboardInfoSuccess } from '../../../state/dashboard/dashboard.selectors';
import { OnboardingLeadRow, OnboardingService } from '../../../services/onboarding.service';
import { PopupService } from '../../../services/popup.service';
import { UnlockEmailConfirmModalComponent } from '../../lead-pipelines/components/unlock-email-confirm-modal/unlock-email-confirm-modal.component';
import { OpenReportModalComponent } from './open-report-modal.component';
import { GenerateReportConfirmModalComponent } from './generate-report-confirm-modal.component';

const MAX_LEADS = 10;

/** Rows shown before the table is folded behind a "View all" toggle. */
const COLLAPSED_LEADS = 5;

/** How often to re-check generation_status while the spinner is showing. */
const GENERATION_POLL_MS = 4000;

@Component({
  selector: 'app-dashboard-top-leads',
  standalone: true,
  imports: [
    NgForOf,
    NgIf,
    TranslateModule,
    UnlockEmailConfirmModalComponent,
    OpenReportModalComponent,
    GenerateReportConfirmModalComponent,
  ],
  templateUrl: './dashboard-top-leads.component.html',
  styleUrl: './dashboard-top-leads.component.scss'
})
export class DashboardTopLeadsComponent implements OnInit, OnDestroy {
  leads: OnboardingLeadRow[] = [];
  isLoading = true;
  isExpanded = false;
  generationStatus: string | null = null;
  totalGenerated = 0;

  hasIcp = false;
  icpOptimization: number | null = null;

  unlockTarget: OnboardingLeadRow | null = null;
  showOpenReportModal = false;
  openReportLeadId: number | null = null;
  openReportLeadName = '';
  /** Report id resolved from the real lead at click time — see onOpenReportClick(). */
  openReportSearchId: number | null = null;

  /** Lead awaiting confirmation in the "generate report" credit-cost modal. */
  generateReportTarget: OnboardingLeadRow | null = null;

  /** Leads currently being enhanced (report generation in flight). */
  enhancingLeads = new Set<number>();
  /** Leads whose report finished generating — maps leadId to searchId. */
  enhancedLeads = new Map<number, number>();
  /** Leads whose email unlock request is in flight. */
  unlockingLeads = new Set<number>();

  /** Placeholder rows rendered while leads are being generated. */
  skeletonRows = [1, 2, 3];

  private destroy$ = new Subject<void>();

  /** Leads whose avatar failed to load, so the initials stand in from then on. */
  private photoFailures = new Set<number>();

  /** Leads streamed over the websocket, keyed by id so they can't duplicate the API rows. */
  private liveLeads = new Map<number, OnboardingLeadRow>();
  private fetchedLeads: OnboardingLeadRow[] = [];
  private generationPollSub: Subscription | null = null;

  constructor(
    private onboardingService: OnboardingService,
    private store: Store<AppState>,
    private router: Router,
    private popupService: PopupService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.fetchLeads();

    // Leads generated right after onboarding arrive over the websocket while the user is
    // already on the dashboard, so rows appear without a refetch.
    this.store.select(OnboardingSelectors.selectLeadItems)
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => {
        for (const item of items ?? []) {
          if (item && typeof item['id'] === 'number') {
            this.liveLeads.set(item['id'], item as OnboardingLeadRow);
          }
        }
        this.mergeLeads();
      });

    // generation_status rides on the lead-generated broadcast. Without this the spinner
    // stayed up forever whenever the run finished with fewer than MAX_LEADS.
    this.store.select(OnboardingSelectors.selectLeadGenerationStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        if (status) {
          this.generationStatus = status;
          this.syncGenerationPolling();
        }
      });

    // The ICP list and dashboard info are already loaded by DashboardComponent, which is
    // the only host of this card, so these are read rather than dispatched again.
    this.store.select(IcpSelectors.selectIcps)
      .pipe(takeUntil(this.destroy$))
      .subscribe((icps) => {
        this.hasIcp = (icps ?? []).length > 0;
      });

    this.store.select(selectDashboardInfoSuccess)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        const completion = result?.dashboard?.profile_completion;
        this.icpOptimization = typeof completion === 'number' ? completion : null;
      });
  }

  ngOnDestroy(): void {
    this.stopGenerationPolling();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Hidden entirely for users with no onboarding run, so the dashboard is unchanged for
   * everyone who did not come through the onboarding flow. Visible immediately when
   * generation is known to be in progress (from the store) even before the API responds.
   */
  get isVisible(): boolean {
    if (this.isGenerating) {
      return true;
    }

    // While the initial fetch is in flight, show the section if we already know
    // onboarding was completed (the store carries the status from completion).
    if (this.isLoading && this.generationStatus !== null) {
      return true;
    }

    return !this.isLoading && this.leads.length > 0;
  }

  get isGenerating(): boolean {
    return this.generationStatus === 'pending' || this.generationStatus === 'processing';
  }

  get hasLeads(): boolean {
    return this.leads.length > 0;
  }

  get visibleLeads(): OnboardingLeadRow[] {
    return this.isExpanded ? this.leads : this.leads.slice(0, COLLAPSED_LEADS);
  }

  get isFoldable(): boolean {
    return this.leads.length > COLLAPSED_LEADS;
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
  }

  /**
   * True only once the dashboard confirms a complete ICP, so the step is never shown as
   * done while the completion figure is still loading.
   */
  get isIcpOptimized(): boolean {
    return this.hasIcp && this.icpOptimization === 100;
  }

  photoUrl(lead: OnboardingLeadRow): string | null {
    const url = lead.person_photo_url?.trim();

    return url && !this.photoFailures.has(lead.id) ? url : null;
  }

  /**
   * Apollo avatars point at LinkedIn's CDN and the signed URLs expire, so a broken image
   * has to degrade to initials rather than leave an empty box.
   */
  onPhotoError(lead: OnboardingLeadRow): void {
    this.photoFailures.add(lead.id);
  }

  initials(lead: OnboardingLeadRow): string {
    const source = [lead.person_first_name, lead.person_last_name]
      .filter(part => !!part?.trim())
      .join(' ')
      || lead.person_name?.trim()
      || '';

    const letters = source
      .split(/\s+/)
      .filter(part => /[a-z0-9]/i.test(part))
      .slice(0, 2)
      .map(part => part[0])
      .join('');

    return letters ? letters.toUpperCase() : '?';
  }

  displayName(lead: OnboardingLeadRow): string {
    const name = lead.person_name?.trim();

    if (name) {
      return name;
    }

    const composed = [lead.person_first_name, lead.person_last_name]
      .filter(part => !!part?.trim())
      .join(' ');

    return composed || 'Unknown contact';
  }

  employeeCount(lead: OnboardingLeadRow): string | null {
    return lead.company_employee_count ? lead.company_employee_count.toLocaleString() : null;
  }

  /**
   * Onboarding leads are stored unscored until the user finishes their ICP, so a numeric
   * score only exists once that happens.
   */
  score(lead: OnboardingLeadRow): number | null {
    const raw = lead.icp_score;

    if (raw === null || raw === undefined || raw === '') {
      return null;
    }

    const parsed = Number(raw);

    return Number.isFinite(parsed) ? Math.round(parsed) : null;
  }

  trackById(_index: number, lead: OnboardingLeadRow): number {
    return lead.id;
  }

  // ─── Phone & Email column logic ────────────────────────────────────

  emailStatus(lead: OnboardingLeadRow): 'locked' | 'unlocked' | 'not_found' {
    const status = lead.email_status;
    if (status === 'unlocked' || status === 'not_found' || status === 'locked') {
      return status;
    }
    return 'locked';
  }

  isUnlocking(lead: OnboardingLeadRow): boolean {
    return this.unlockingLeads.has(lead.id);
  }

  isEnhanced(lead: OnboardingLeadRow): boolean {
    return !!(lead.is_enhanced || this.enhancedLeads.has(lead.id));
  }

  isEnhancing(lead: OnboardingLeadRow): boolean {
    return this.enhancingLeads.has(lead.id);
  }

  reportId(lead: OnboardingLeadRow): number | null {
    return this.enhancedLeads.get(lead.id) ?? lead.enhanced_report_id ?? null;
  }

  startUnlock(lead: OnboardingLeadRow): void {
    if (this.emailStatus(lead) !== 'locked' || this.isUnlocking(lead)) {
      return;
    }
    this.unlockTarget = lead;
  }

  confirmUnlock(): void {
    const lead = this.unlockTarget;
    this.unlockTarget = null;

    if (!lead || this.unlockingLeads.has(lead.id)) {
      return;
    }

    this.unlockingLeads.add(lead.id);

    this.onboardingService.unlockLeadEmail(lead.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.unlockingLeads.delete(lead.id);
          this.applyEmailUnlock(lead.id, result.person_email, result.email_status || 'unlocked');
          this.popupService.notify(
            this.translate.instant('lead_pipelines.toasts.email_unlocked'),
            '',
            'success',
          );
        },
        error: (error) => {
          this.unlockingLeads.delete(lead.id);

          // The backend already persists the real outcome (e.g. flips the lead to
          // "not_found") before responding with an error status — it now reports that
          // in the body so the row can be patched immediately instead of only catching
          // up on the next fetch/poll or a manual page reload.
          const newStatus = error?.error?.email_status;
          if (newStatus && newStatus !== lead.email_status) {
            this.applyEmailUnlock(lead.id, newStatus === 'unlocked' ? lead.person_email : null, newStatus);
          }

          const msg = error?.error?.message
            || this.translate.instant('lead_pipelines.errors.generic');
          this.popupService.notify(msg, '', 'error');
        }
      });
  }

  /**
   * Patch both the merged table rows and the fetch/live caches so a later
   * merge or poll cannot wipe the unlocked email.
   */
  private applyEmailUnlock(leadId: number, email: string, status: string): void {
    const patch = (row: OnboardingLeadRow | undefined): OnboardingLeadRow | undefined => {
      if (!row) {
        return row;
      }
      return {
        ...row,
        person_email: email,
        email_status: status,
      };
    };

    this.fetchedLeads = this.fetchedLeads.map(l => (l.id === leadId ? patch(l)! : l));
    if (this.liveLeads.has(leadId)) {
      this.liveLeads.set(leadId, patch(this.liveLeads.get(leadId))!);
    }
    this.leads = this.leads.map(l => (l.id === leadId ? patch(l)! : l));
  }

  /** Opens the credit-cost confirm modal; the actual generate call only fires on confirm. */
  startGenerateReport(lead: OnboardingLeadRow): void {
    if (this.enhancingLeads.has(lead.id) || this.isEnhanced(lead)) {
      return;
    }

    this.generateReportTarget = lead;
  }

  /**
   * Generates the report, then — unlike the legacy generate→"Open report" two-click flow —
   * immediately drops the lead from the onboarding list, marks it converted, and takes the
   * user to My Reports with a toast, since the report itself is still generating in the
   * background and isn't ready to view yet.
   */
  confirmGenerateReport(): void {
    const lead = this.generateReportTarget;
    this.generateReportTarget = null;

    if (!lead || this.enhancingLeads.has(lead.id) || this.isEnhanced(lead)) {
      return;
    }

    const leadName = this.displayName(lead);

    this.enhancingLeads.add(lead.id);

    this.onboardingService.enhanceLead(lead.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.enhancingLeads.delete(lead.id);
          this.enhancedLeads.set(lead.id, result.search_id);

          this.leads = this.leads.filter(l => l.id !== lead.id);
          this.fetchedLeads = this.fetchedLeads.filter(l => l.id !== lead.id);
          this.liveLeads.delete(lead.id);

          this.onboardingService.markLeadConverted(lead.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              error: (error) => {
                // Report generation itself succeeded — this only controls whether the lead
                // reappears in the onboarding table on a later fetch (it'll then fall back to
                // the "Open report" flow above, which resolves its own search id independently).
                console.error('Failed to mark onboarding lead as converted', { leadId: lead.id, error });
              }
            });

          this.popupService.notify(`Report of ${leadName} is generating`, '', 'info');
          this.router.navigate(['/reports']);
        },
        error: (error) => {
          this.enhancingLeads.delete(lead.id);
          this.popupService.notify(this.enhanceErrorMessage(error), '', 'error');
        }
      });
  }

  /**
   * Maps the enhance endpoint's failure statuses (402 insufficient credits, 400 no LinkedIn
   * URL, 409 already enhanced) to a user-facing message so a failed generate is never silent.
   */
  private enhanceErrorMessage(error: any): string {
    const status = error?.status;
    const backendMessage = error?.error?.error;

    if (status === 402) {
      return 'Not enough credits to generate this report.';
    }
    if (status === 400) {
      return 'This lead has no LinkedIn profile, so a report cannot be generated.';
    }
    if (status === 409) {
      return 'A report has already been generated for this lead.';
    }

    return backendMessage || 'Failed to generate the report. Please try again.';
  }

  onOpenReportClick(lead: OnboardingLeadRow): void {
    this.openReportLeadId = lead.id;
    this.openReportLeadName = this.displayName(lead);
    // Resolve the search id from the real lead now, while it's still on hand — lead.enhanced_report_id
    // comes straight from the backend for leads that arrive already-enhanced (e.g. after a page
    // reload), which the local enhancedLeads map alone can't cover since it's only populated by
    // this component instance's own confirmGenerateReport() calls.
    this.openReportSearchId = this.reportId(lead);
    this.showOpenReportModal = true;
  }

  /**
   * Fallback "Open report" flow for leads that arrive already-enhanced (is_enhanced true from
   * the backend) without having gone through this session's confirmGenerateReport() — e.g. a
   * generate that didn't finish converting the lead before the user navigated away, or a lead
   * enhanced in a previous session. The report already exists, so this always navigates to it;
   * it does not gate on markLeadConverted() succeeding first.
   */
  confirmOpenReport(): void {
    const leadId = this.openReportLeadId;
    const searchId = this.openReportSearchId;

    this.showOpenReportModal = false;
    this.openReportLeadId = null;
    this.openReportSearchId = null;

    if (!leadId || !searchId) {
      this.popupService.notify('Could not find that report. Please refresh and try again.', '', 'error');
      return;
    }

    this.leads = this.leads.filter(l => l.id !== leadId);
    this.fetchedLeads = this.fetchedLeads.filter(l => l.id !== leadId);
    this.liveLeads.delete(leadId);

    this.onboardingService.markLeadConverted(leadId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (error) => {
          // The report is already generated and about to open regardless — this only
          // controls whether the lead reappears in the onboarding table later, so a
          // failure here shouldn't block navigation. Logged so it's not silently lost.
          console.error('Failed to mark onboarding lead as converted', { leadId, error });
        }
      });

    this.router.navigate(['/report', searchId]);
  }

  /**
   * Combine the API rows with anything streamed since, newest first. Ids grow with
   * insertion order, so sorting by id descending keeps the freshest leads on top without
   * depending on created_at being present in the broadcast payload.
   */
  private mergeLeads(): void {
    const merged = new Map<number, OnboardingLeadRow>();

    for (const lead of this.fetchedLeads) {
      if (lead && typeof lead.id === 'number') {
        merged.set(lead.id, lead);
      }
    }

    for (const [id, lead] of this.liveLeads) {
      const existing = merged.get(id);
      const next: OnboardingLeadRow = { ...existing, ...lead };

      // A websocket row never carries the paid email — keep a local unlock.
      if (existing?.email_status === 'unlocked' && next.email_status !== 'unlocked') {
        next.email_status = existing.email_status;
        next.person_email = existing.person_email;
      }

      merged.set(id, next);
    }

    this.leads = Array.from(merged.values())
      .sort((a, b) => b.id - a.id)
      .slice(0, MAX_LEADS);
  }

  private fetchLeads(): void {
    this.onboardingService.getOnboardingLeads(MAX_LEADS)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.fetchedLeads = response.leads ?? [];
          this.generationStatus = response.generation_status;
          this.totalGenerated = response.leads_generated;
          this.isLoading = false;
          this.mergeLeads();
          this.syncGenerationPolling();
        },
        // A dashboard card must never break the page it sits on: on failure the card
        // simply stays hidden unless websocket leads arrive.
        error: () => {
          this.isLoading = false;
          this.mergeLeads();
          this.syncGenerationPolling();
        }
      });
  }

  /**
   * Keep re-fetching while the run is in flight. The websocket alone is not enough:
   * generation_status used to stay "processing" forever when fewer than 10 leads were
   * produced, and a reload mid-run has no live events to clear the spinner.
   */
  private syncGenerationPolling(): void {
    if (this.isGenerating) {
      this.startGenerationPolling();
    } else {
      this.stopGenerationPolling();
    }
  }

  private startGenerationPolling(): void {
    if (this.generationPollSub) {
      return;
    }

    this.generationPollSub = interval(GENERATION_POLL_MS)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.onboardingService.getOnboardingLeads(MAX_LEADS))
      )
      .subscribe({
        next: (response) => {
          this.fetchedLeads = response.leads ?? [];
          this.generationStatus = response.generation_status;
          this.totalGenerated = response.leads_generated;
          this.mergeLeads();
          this.syncGenerationPolling();
        },
        error: () => {
          // Leave the current spinner state alone; the next tick will try again.
        }
      });
  }

  private stopGenerationPolling(): void {
    this.generationPollSub?.unsubscribe();
    this.generationPollSub = null;
  }
}
